import { AssistantPanel } from './AssistantPanel';
import { WorkConversationPanel } from '@/components/work/WorkConversationPanel';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AtSign, CircleAlert, MessageSquareText, SendHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ChatMarkdownRenderer } from '@/components/chat/ChatMarkdownRenderer';
import { Textarea } from '@/components/ui/Textarea';
import { invokeDashboardChat, streamDashboardChat } from '@/lib/dashboard-chat';
import { AppChatHeader } from '@/components/layout/AppChatHeader';
import { AppChatHistory } from '@/components/layout/AppChatHistory';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import {
  useAppAssistant,
  type AppAssistantContextSnapshot,
} from '@/lib/app-assistant-context';
import { useStickyChatScroll } from '@/lib/hooks/useStickyChatScroll';
import { cn } from '@/lib/utils';
import {
  AppChatAdHocResultCard,
  AppChatHandoffCard,
  useRunHandoffAction,
} from '@/components/layout/AppChatResultCards';
import { AppChatActionProposalCard } from '@/components/layout/AppChatActionProposalCard';
import { AppChatAppRecordCard } from '@/components/layout/AppChatAppRecordCard';
import { AppChatImportCard } from '@/components/layout/AppChatImportCard';
import { AppChatLaunchCard } from '@/components/layout/AppChatLaunchCard';
import { ThreadReplacementProposalCard } from '@/components/layout/ThreadReplacementProposalCard';
import { ThreadWaitProposalCard } from '@/components/layout/ThreadWaitProposalCard';
import type { ToolActionConfirmationContext } from '@/components/views/atoms/ToolActionConfirmationDialog';
import type {
  DashboardChatMessage,
  DashboardChatResult,
  DashboardChatThreadInteraction,
  DashboardChatThreadReplacementProposalResult,
  DashboardChatThreadWaitProposalResult,
} from '@shared/types/dashboard-chat';
import type { ResolveThreadChatWaitSelection } from '@shared/types/thread-work';
import type { ChatSource } from '@shared/types/chat-sources';
import { chatSourceKey } from '@shared/types/chat-sources';
import { latestCitedSources } from '@/lib/chat-sources';
import { SourceChips, SourcePicker } from '@/components/chat-sources/SourcePicker';
import { SourcedAnswerSources } from '@/components/chat-sources/SourcedAnswerSources';
import { useSourcePicker } from '@/components/chat-sources/useSourcePicker';
import { useChatFileDrop } from '@/components/chat-sources/useChatFileDrop';
import { SaveAnswerMenu } from '@/components/chat-sources/SaveAnswerMenu';

const LOCAL_POPUP_MESSAGE_PREFIX = 'local-popup-';

const getUserFacingChatError = (error: unknown, fallback: string): string => {
  if (!(error instanceof Error)) return fallback;
  if (/failed to fetch|networkerror|load failed/i.test(error.message)) {
    return 'Le chat n’est pas disponible pour le moment.';
  }
  return error.message;
};

const getDefaultPageContext = (): AppAssistantContextSnapshot => ({
  surface: 'home' as const,
  routePath: window.location.pathname,
  routeLabel: 'Domino',
  visibleSummary:
    'Vue Domino courante. Répondez seulement avec les informations visibles ou accessibles à cet utilisateur.',
  visibleEntities: [],
});

const shouldAutoStartResult = (result: DashboardChatResult): boolean =>
  'autoStart' in result && result.autoStart === true && result.type !== 'action_proposal';

const findLatestActionProposalMessageId = (
  messages: DashboardChatMessage[]
): string | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === 'assistant' && message.result?.type === 'action_proposal') {
      return message.id;
    }
  }
  return null;
};

const findLatestDecisionFocus = (messages: DashboardChatMessage[]) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const focus = messages[index]?.context?.pageContext?.threadDecisionFocus;
    if (focus) return focus;
  }
  return null;
};

const mergeServerMessagesWithLocalPopupMessages = (
  serverMessages: DashboardChatMessage[],
  currentMessages: DashboardChatMessage[]
): DashboardChatMessage[] => {
  const serverIds = new Set(serverMessages.map((message) => message.id));
  const localMessages = currentMessages.filter(
    (message) =>
      message.id.startsWith(LOCAL_POPUP_MESSAGE_PREFIX) && !serverIds.has(message.id)
  );
  return [...serverMessages, ...localMessages].sort(
    (left, right) =>
      new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
};

const GeneralAppChatPanel = () => {
  const runHandoffAction = useRunHandoffAction();
  const {
    collapsed,
    decisionFocus,
    pageContext,
    pendingCommand,
    clearPendingCommand,
    clearDecisionFocus,
    closeAssistant,
    openAssistant,
    pinDecisionFocus,
    setActiveConversationId,
  } = useAppAssistant();
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  // 22/10: the chat opens on a new, empty conversation; the past ones are in the history.
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DashboardChatMessage[]>([]);
  const [composerValue, setComposerValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingAnswer, setStreamingAnswer] = useState<string | null>(null);
  // D1: sources cited with « @ »; they follow the conversation until removed.
  const [sources, setSources] = useState<ChatSource[]>([]);
  const [phase, setPhase] = useState<string | null>(null);
  const sourcePicker = useSourcePicker({ value: composerValue, setValue: setComposerValue, sources, setSources });
  const fileDrop = useChatFileDrop((source) => setSources((current) =>
    current.some((item) => chatSourceKey(item.ref) === chatSourceKey(source.ref)) ? current : [...current, source]));
  const [historyOpen, setHistoryOpen] = useState(false);
  const [resolvingReplacementId, setResolvingReplacementId] = useState<string | null>(
    null
  );
  const [resolvingWaitId, setResolvingWaitId] = useState<string | null>(null);
  const [resultErrorsByMessageId, setResultErrorsByMessageId] = useState<
    Record<string, string>
  >({});
  const [autoPrepareMessageId, setAutoPrepareMessageId] = useState<string | null>(null);
  const [activeConfirmationContext, setActiveConfirmationContext] =
    useState<ToolActionConfirmationContext | null>(null);

  const context = pageContext ?? getDefaultPageContext();
  const currentPageContext = useMemo(
    () => ({
      routePath: context.routePath,
      routeLabel: context.routeLabel,
      visibleSummary: context.visibleSummary,
      visibleEntities: context.visibleEntities,
      visibleError: context.visibleError ?? null,
      selectedFlowId: context.selectedFlowId ?? null,
      forgeConversationId: context.forgeConversationId ?? null,
      draftTitle: context.draftTitle ?? null,
      visibleItems: context.visibleItems ?? [],
      availableActions: context.availableActions ?? [],
      threadDecisionFocus: decisionFocus,
      appObject: context.appObject ?? null,
    }),
    [
      context.appObject,
      context.availableActions,
      context.draftTitle,
      context.forgeConversationId,
      context.routeLabel,
      context.routePath,
      context.selectedFlowId,
      context.visibleEntities,
      context.visibleError,
      context.visibleItems,
      context.visibleSummary,
      decisionFocus,
    ]
  );
  const effectiveCurrentPageContext = useMemo(() => {
    if (!activeConfirmationContext) return currentPageContext;
    const selected =
      activeConfirmationContext.selectedLabels.length > 0
        ? activeConfirmationContext.selectedLabels.join(', ')
        : 'aucune ligne sélectionnée';
    const editable =
      activeConfirmationContext.editableLabels.length > 0
        ? activeConfirmationContext.editableLabels.join(', ')
        : 'aucun champ éditable';
    const memo = activeConfirmationContext.editablePreview
      ? activeConfirmationContext.editablePreview.slice(0, 900)
      : 'vide';
    return {
      ...currentPageContext,
      visibleSummary: [
        currentPageContext.visibleSummary,
        `Proposition de confirmation ouverte : ${activeConfirmationContext.title}.`,
        `Résumé : ${activeConfirmationContext.summary}`,
        `Lignes sélectionnées : ${selected}.`,
        `Champs éditables : ${editable}.`,
        `Contenu éditable courant : ${memo}`,
      ].join('\n'),
    };
  }, [activeConfirmationContext, currentPageContext]);

  const requestContext = {
    surface: context.surface,
    mode: 'ad_hoc' as const,
    currentDashboardId: context.dashboardId ?? null,
    currentToolKey: context.toolKey ?? null,
    currentToolTitle: context.toolTitle ?? null,
    currentExecutionId: context.executionId ?? null,
    currentExecutionLabel: context.executionLabel ?? null,
    currentPageContext: effectiveCurrentPageContext,
  };
  const latestMessagePageContext = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const page = messages[index]?.context?.pageContext;
      if (page?.routePath && page.routeLabel) {
        return {
          routePath: page.routePath,
          routeLabel: page.routeLabel,
        };
      }
    }
    return null;
  }, [messages]);
  const contextShiftNotice =
    latestMessagePageContext &&
    latestMessagePageContext.routePath !== currentPageContext.routePath
      ? {
          previousLabel: latestMessagePageContext.routeLabel,
          currentLabel: currentPageContext.routeLabel,
        }
      : null;
  const {
    scrollRef: messageListRef,
    handleScroll: handleMessageListScroll,
    resumeAutoScroll,
  } = useStickyChatScroll<HTMLDivElement>({
    watch: [messages, loading, streamingAnswer],
  });

  const persistConversation = (nextConversationId: string | null) => {
    setConversationId(nextConversationId);
    setActiveConversationId(nextConversationId);
  };

  const loadConversation = async (options: { conversationId: string }) => {
    resumeAutoScroll();
    setLoading(true);
    setError(null);
    setStreamingAnswer(null);

    try {
      const response = await invokeDashboardChat({
        action: 'resume',
        conversationId: options.conversationId,
        ...requestContext,
      });

      persistConversation(response.conversationId);
      setMessages(response.messages);
      setSources(latestCitedSources(response.messages));
      const restoredFocus = findLatestDecisionFocus(response.messages);
      if (restoredFocus) pinDecisionFocus(restoredFocus);
      else clearDecisionFocus();
      setAutoPrepareMessageId(null);
      setActiveConfirmationContext(null);
    } catch (loadError) {
      setError(
        getUserFacingChatError(loadError, 'Impossible de charger le chat Domino.')
      );
    } finally {
      setLoading(false);
    }
  };

  /** Nothing is written until the first message: an empty conversation never enters the history. */
  const showEmptyConversation = () => {
    persistConversation(null);
    setMessages([]);
    setSources([]);
    setError(null);
    setStreamingAnswer(null);
    setAutoPrepareMessageId(null);
    setActiveConfirmationContext(null);
    setResultErrorsByMessageId({});
  };

  // Reopening the collapsed chat lands on a new conversation, like the first opening.
  const wasCollapsedRef = useRef(collapsed);
  useEffect(() => {
    if (wasCollapsedRef.current && !collapsed && conversationId && !sending) {
      setHistoryOpen(false);
      showEmptyConversation();
    }
    if (collapsed) setFullscreen(false);
    wasCollapsedRef.current = collapsed;
  }, [collapsed]);

  // Full screen shows the history on the left; the side panel goes back to the messages.
  useEffect(() => {
    setHistoryOpen(fullscreen);
  }, [fullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) setFullscreen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [fullscreen]);

  const startNewConversation = () => {
    if (!fullscreen) setHistoryOpen(false);
    clearDecisionFocus();
    showEmptyConversation();
    window.setTimeout(() => composerRef.current?.focus(), 80);
  };

  const selectConversation = async (nextConversationId: string) => {
    if (!fullscreen) setHistoryOpen(false);
    if (nextConversationId === conversationId) return;
    await loadConversation({ conversationId: nextConversationId });
  };

  const handleConversationDeleted = (deletedConversationId: string) => {
    if (deletedConversationId === conversationId) showEmptyConversation();
  };

  const sendMessage = async (
    overrideText?: string,
    threadInteraction?: DashboardChatThreadInteraction
  ) => {
    const text = (overrideText ?? composerValue).trim();
    if (!text || sending) return;

    if (!overrideText && activeConfirmationContext?.applyChatEdit(text)) {
      resumeAutoScroll();
      setError(null);
      setComposerValue('');
      const createdAt = new Date().toISOString();
      const optimisticUserMessage: DashboardChatMessage = {
        id: `${LOCAL_POPUP_MESSAGE_PREFIX}${crypto.randomUUID()}`,
        role: 'user',
        content: text,
        createdAt,
      };
      const assistantMessage: DashboardChatMessage = {
        id: `${LOCAL_POPUP_MESSAGE_PREFIX}${crypto.randomUUID()}`,
        role: 'assistant',
        content:
          'J’ai mis à jour la proposition ouverte. Elle ne sera envoyée qu’après ta confirmation.',
        createdAt: new Date().toISOString(),
      };
      setMessages((current) => [...current, optimisticUserMessage, assistantMessage]);
      window.setTimeout(() => composerRef.current?.focus(), 60);
      return;
    }

    resumeAutoScroll();
    setSending(true);
    setError(null);
    setComposerValue('');
    setStreamingAnswer('');
    setPhase(null);
    sourcePicker.close();

    const optimisticUserMessage: DashboardChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    };
    setMessages((current) => [...current, optimisticUserMessage]);

    try {
      const response = await streamDashboardChat(
        {
          action: 'send',
          ...(conversationId ? { conversationId } : { newConversation: true }),
          message: text,
          ...(threadInteraction ? { threadInteraction } : {}),
          ...(sources.length ? { sources } : {}),
          ...requestContext,
        },
        {
          onChunk: (chunk) => setStreamingAnswer((current) => `${current ?? ''}${chunk}`),
          onPhase: setPhase,
        }
      );
      // The server reloads the cited sources: a deleted one leaves the chips.
      if (sources.length) setSources(latestCitedSources(response.messages));

      persistConversation(response.conversationId);
      setMessages((current) =>
        mergeServerMessagesWithLocalPopupMessages(response.messages, current)
      );
      const responseFocus = findLatestDecisionFocus(response.messages);
      if (responseFocus) pinDecisionFocus(responseFocus);
      setAutoPrepareMessageId(findLatestActionProposalMessageId(response.messages));
      if (response.status === 'answered' && shouldAutoStartResult(response.result)) {
        window.setTimeout(() => runHandoffAction(response.result), 0);
      }
      setStreamingAnswer(null);
    } catch (sendError) {
      setError(getUserFacingChatError(sendError, 'Impossible d’envoyer ce message.'));
      setComposerValue(text);
      setStreamingAnswer(null);
    } finally {
      setSending(false);
      setPhase(null);
    }
  };

  useEffect(() => {
    if (!pendingCommand || sending || loading) return;
    clearPendingCommand(pendingCommand.id);
    void sendMessage(pendingCommand.text, pendingCommand.threadInteraction);
  }, [clearPendingCommand, loading, pendingCommand, sending]);

  const resolveReplacement = async (params: {
    messageId: string;
    result: DashboardChatThreadReplacementProposalResult;
    resolution: 'confirm' | 'reject';
  }) => {
    if (!conversationId || params.result.proposal.status !== 'pending') return;
    setResolvingReplacementId(params.result.proposal.id);
    setResultErrorsByMessageId((current) => {
      const next = { ...current };
      delete next[params.messageId];
      return next;
    });
    try {
      const response = await invokeDashboardChat({
        action: 'resolve_thread_replacement',
        conversationId,
        proposalId: params.result.proposal.id,
        resolution: params.resolution,
        reason:
          params.resolution === 'confirm'
            ? 'Confirmation explicite depuis le chat.'
            : 'La décision actuelle est conservée explicitement.',
        idempotencyKey: `replacement:${params.result.proposal.id}:${params.resolution}`,
      });
      setMessages((current) =>
        mergeServerMessagesWithLocalPopupMessages(response.messages, current)
      );
      if (params.resolution === 'confirm') clearDecisionFocus();
    } catch (resolveError) {
      setResultErrorsByMessageId((current) => ({
        ...current,
        [params.messageId]:
          resolveError instanceof Error
            ? resolveError.message
            : 'Impossible de résoudre cette proposition de remplacement.',
      }));
    } finally {
      setResolvingReplacementId(null);
    }
  };

  const renderThreadReplacementAttachment = (
    message: DashboardChatMessage,
    result: DashboardChatThreadReplacementProposalResult
  ) => (
    <ThreadReplacementProposalCard
      result={result}
      resolving={resolvingReplacementId === result.proposal.id}
      resolutionInProgress={resolvingReplacementId !== null}
      error={resultErrorsByMessageId[message.id] ?? null}
      onConfirm={() =>
        void resolveReplacement({
          messageId: message.id,
          result,
          resolution: 'confirm',
        })
      }
      onReject={() =>
        void resolveReplacement({
          messageId: message.id,
          result,
          resolution: 'reject',
        })
      }
    />
  );

  const resolveWait = async (params: {
    messageId: string;
    result: DashboardChatThreadWaitProposalResult;
    resolution: 'confirm' | 'reject';
    selection?: ResolveThreadChatWaitSelection;
  }) => {
    if (!conversationId || params.result.proposal.status !== 'pending') return;
    setResolvingWaitId(params.result.proposal.id);
    setResultErrorsByMessageId((current) => {
      const next = { ...current };
      delete next[params.messageId];
      return next;
    });
    try {
      const response = await invokeDashboardChat({
        action: 'resolve_thread_wait',
        conversationId,
        proposalId: params.result.proposal.id,
        resolution: params.resolution,
        ...(params.selection ? { selection: params.selection } : {}),
        idempotencyKey: `wait-resolution:${params.result.proposal.id}:${params.resolution}`,
      });
      setMessages((current) =>
        mergeServerMessagesWithLocalPopupMessages(response.messages, current)
      );
      if (params.resolution === 'confirm') clearDecisionFocus();
    } catch (resolveError) {
      setResultErrorsByMessageId((current) => ({
        ...current,
        [params.messageId]:
          resolveError instanceof Error
            ? resolveError.message
            : 'Impossible de résoudre cette proposition d’attente.',
      }));
    } finally {
      setResolvingWaitId(null);
    }
  };

  const renderThreadWaitAttachment = (
    message: DashboardChatMessage,
    result: DashboardChatThreadWaitProposalResult
  ) => (
    <ThreadWaitProposalCard
      result={result}
      resolving={resolvingWaitId === result.proposal.id}
      resolutionInProgress={resolvingWaitId !== null}
      error={resultErrorsByMessageId[message.id] ?? null}
      onConfirm={(selection) =>
        void resolveWait({
          messageId: message.id,
          result,
          resolution: 'confirm',
          selection,
        })
      }
      onReject={() =>
        void resolveWait({
          messageId: message.id,
          result,
          resolution: 'reject',
        })
      }
    />
  );

  const renderAssistantResultAttachment = (message: DashboardChatMessage) => {
    const result = message.result;
    if (!result) return null;

    if (result.type === 'ad_hoc_result') {
      return <AppChatAdHocResultCard result={result} error={resultErrorsByMessageId[message.id] ?? null} />;
    }

    if (result.type === 'app_record_proposal') {
      return <AppChatAppRecordCard result={result} />;
    }

    if (result.type === 'launch_proposal') {
      return <AppChatLaunchCard result={result} />;
    }

    if (result.type === 'import_proposal') {
      return <AppChatImportCard result={result} />;
    }

    if (result.type === 'sourced_answer') {
      return <SourcedAnswerSources result={result} />;
    }

    if (result.type === 'action_proposal') {
      return (
        <AppChatActionProposalCard
          messageId={message.id}
          action={result.action}
          autoPrepare={message.id === autoPrepareMessageId}
          onConfirmationContextChange={setActiveConfirmationContext}
        />
      );
    }

    if (result.type === 'thread_replacement_proposal') {
      return renderThreadReplacementAttachment(message, result);
    }

    if (result.type === 'thread_wait_proposal') {
      return renderThreadWaitAttachment(message, result);
    }

    return <AppChatHandoffCard result={result} onRun={() => runHandoffAction(result)} />;
  };

  if (collapsed) {
    return (
      <aside className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-2xl border border-domino-border bg-domino-warm shadow-[var(--domino-shadow-elevated)]">
        <button
          type="button"
          onClick={openAssistant}
          className="flex h-11 w-11 items-center justify-center rounded-2xl bg-domino-lilac text-domino-aubergine shadow-sm transition hover:bg-domino-muted"
          aria-label="Ouvrir le chat Domino"
          title="Chat Domino (⌘K)"
        >
          <MessageSquareText className="h-5 w-5" />
        </button>
      </aside>
    );
  }

  const history = historyOpen ? (
    <AppChatHistory
      activeConversationId={conversationId}
      onSelect={(id) => void selectConversation(id)}
      onDeleted={handleConversationDeleted}
    />
  ) : null;

  return (
    <AssistantPanel label="Assistant Domino" fullscreen={fullscreen}>
      <AppChatHeader
        routeLabel={context.routeLabel}
        historyOpen={historyOpen}
        fullscreen={fullscreen}
        decisionFocus={decisionFocus}
        onNewConversation={startNewConversation}
        onToggleHistory={() => setHistoryOpen((current) => !current)}
        onToggleFullscreen={() => setFullscreen((current) => !current)}
        onCollapse={closeAssistant}
        onClearDecisionFocus={clearDecisionFocus}
      />

      {history && !fullscreen ? (
        <div className="min-h-0 flex-1 overflow-y-auto">{history}</div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {history && (
            <div className="w-72 shrink-0 overflow-y-auto border-r border-domino-border">{history}</div>
          )}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div
              ref={messageListRef}
              className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,#FAF7F0_0%,#FDFBF7_100%)] px-4 py-4"
              onScroll={handleMessageListScroll}
            >
              {loading ? (
                <SkeletonGroup label="Chargement de la conversation" className={cn('space-y-4', fullscreen && 'mx-auto max-w-3xl')}>
                  <Skeleton className="ml-8 h-12 rounded-2xl" />
                  <div className="mr-8 space-y-2 px-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </SkeletonGroup>
              ) : (
                <div className={cn('space-y-4', fullscreen && 'mx-auto max-w-3xl')}>
                  {error && (
                    <div className="flex items-start gap-2 border-b border-red-200 pb-3 text-sm text-red-700">
                      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>{error}</p>
                    </div>
                  )}

                  {messages.map((message, index) => (
                    <div
                      key={message.id}
                      className={cn(
                        'text-sm leading-6',
                        message.role === 'user'
                          ? 'ml-8 rounded-2xl bg-domino-aubergine px-3 py-3 text-white'
                          : 'mr-8 px-1 py-1 text-domino-ink'
                      )}
                    >
                      {message.role === 'user' ? (
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      ) : (
                        <>
                          <ChatMarkdownRenderer content={message.content} />
                          {renderAssistantResultAttachment(message)}
                          {(message.result?.type === 'sourced_answer' || message.result?.type === 'text_answer') && (
                            <SaveAnswerMenu
                              answer={message.result.answer}
                              question={[...messages.slice(0, index)].reverse().find((item) => item.role === 'user')?.content ?? ''}
                              citations={message.result.type === 'sourced_answer' ? message.result.citations : []}
                            />
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {contextShiftNotice && (
                    <div className="border-y border-domino-border py-2 text-xs text-domino-inkSoft">
                      Contexte : {contextShiftNotice.currentLabel}
                      {decisionFocus ? ' · la décision épinglée reste active' : ''}
                    </div>
                  )}

                  {streamingAnswer !== null && (
                    <div className="mr-8 px-1 py-1 text-sm leading-6 text-domino-ink">
                      <ChatMarkdownRenderer content={streamingAnswer || phase || 'Domino réfléchit…'} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              {...fileDrop.dropProps}
              className={cn('border-t border-domino-border bg-domino-warm p-4', fileDrop.dragging && 'bg-domino-lilac')}
            >
              <div className={cn(fullscreen && 'mx-auto max-w-3xl')}>
                {sourcePicker.open && (sourcePicker.searched || sourcePicker.failed || sourcePicker.options.length > 0) && (
                  <SourcePicker
                    options={sourcePicker.options}
                    searched={sourcePicker.searched}
                    active={sourcePicker.active}
                    failed={sourcePicker.failed}
                    onPick={sourcePicker.pick}
                  />
                )}
                {(fileDrop.uploading.length > 0 || fileDrop.error) && (
                  <p className="mb-2 text-sm text-domino-inkSoft">
                    {fileDrop.uploading.map((name) => `Envoi de ${name}…`).join(' · ')}{fileDrop.error}
                  </p>
                )}
                <SourceChips
                  sources={sources}
                  onRemove={(source) => setSources(sources.filter((item) => chatSourceKey(item.ref) !== chatSourceKey(source.ref)))}
                />
                <div className="flex items-end gap-3">
                  <button
                    type="button"
                    aria-label="Citer une source"
                    onClick={() => {
                      const textarea = composerRef.current;
                      const caret = textarea?.selectionStart ?? composerValue.length;
                      const spacer = caret > 0 && !/\s/.test(composerValue[caret - 1] ?? '') ? ' ' : '';
                      const next = `${composerValue.slice(0, caret)}${spacer}@${composerValue.slice(caret)}`;
                      sourcePicker.onChange(next, caret + spacer.length + 1);
                      window.setTimeout(() => {
                        textarea?.focus();
                        textarea?.setSelectionRange(caret + spacer.length + 1, caret + spacer.length + 1);
                      }, 0);
                    }}
                    className="mb-3 shrink-0 rounded-lg p-1 text-domino-inkSoft transition-colors duration-150 hover:text-domino-aubergine"
                  >
                    <AtSign className="h-4 w-4" />
                  </button>
                  <Textarea
                    ref={composerRef}
                    value={composerValue}
                    rows={2}
                    placeholder={
                      activeConfirmationContext
                        ? 'Modifie la proposition ou pose une question.'
                        : 'Pose une question ou donne une instruction.'
                    }
                    className="min-h-[64px] rounded-2xl border-domino-cardBorder bg-domino-muted"
                    onChange={(event) => sourcePicker.onChange(event.target.value, event.target.selectionStart)}
                    onBlur={() => sourcePicker.close()}
                    onKeyDown={(event) => {
                      if (sourcePicker.onKeyDown(event)) return;
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void sendMessage();
                      }
                    }}
                  />
                  <Button
                    className="h-12 w-12 rounded-2xl px-0"
                    disabled={sending || composerValue.trim().length === 0}
                    loading={sending}
                    onClick={() => void sendMessage()}
                  >
                    {!sending && <SendHorizontal className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AssistantPanel>
  );
};

export const AppChatPanel = () => {
  const { workFocus, collapsed } = useAppAssistant();
  return workFocus && !collapsed ? <WorkConversationPanel /> : <GeneralAppChatPanel />;
};
